import emailIcon from "@loot-os/icons/icons/email";
import { Alert } from "../../../../core/Alert.js";
import { Button } from "../../../../core/Button.js";
import { cardStyles } from "../../../../core/Card.styles.js";
import { Icon } from "../../../../core/Icon.js";
import { Input } from "../../../../core/Input.js";
import { Text } from "../../../../core/Text.js";
import { AppLink } from "../../../../foundations/AppLink.js";
import { cn } from "../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../foundations/interaction.js";
import type { RichText } from "../../../shared/content.js";
import type { LoginPanelContent } from "../../loginContent.js";

/**
 * The sign-in panel, in the store's modal look: a raised card with its title, the email field and
 * one full-width action, the consent line under it; once sent, where the link went and a way
 * back to the form.
 */
export function LoginPanel({ title, description, notice, error, step }: LoginPanelContent) {
  return (
    <div
      className={cardStyles({
        radius: "3xl",
        className: "flex w-full flex-col gap-6 p-6 shadow-float sm:p-8",
      })}
    >
      <div className="flex flex-col gap-2">
        <Text as="h1" variant="heading-h1" tone="primary">
          {title}
        </Text>
        <Text variant="body-b2" tone="secondary">
          {description}
        </Text>
      </div>

      {(notice || error) && (
        <div className="flex flex-col gap-2">
          {notice && <Alert tone="danger">{notice}</Alert>}
          {error && <Alert tone="danger">{error}</Alert>}
        </div>
      )}

      {step.kind === "sent" ? (
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-pill bg-accent-subtle text-accent-text">
              <Icon icon={emailIcon} size={20} />
            </span>
            {/* Polite, so a screen reader hears the link went out where the form was. */}
            <p role="status" className="font-body text-body-b1 text-text-secondary">
              {step.message.before}
              <span className="font-semibold break-all text-text-primary">
                {step.message.email}
              </span>
              {step.message.after}
            </p>
          </div>
          <Button variant="secondary" fullWidth onClick={step.reset.onReset}>
            {step.reset.label}
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            step.onSubmit();
          }}
        >
          <Input
            label={step.email.label}
            type="email"
            autoComplete="email"
            required
            size="lg"
            placeholder={step.email.placeholder}
            value={step.email.value}
            onChange={(event) => step.email.onChange(event.target.value)}
          />
          <Button
            type="submit"
            variant="brand"
            size="lg"
            fullWidth
            loading={step.submit.busy}
            disabled={step.submit.busy}
          >
            {step.submit.busy ? step.submit.busyLabel : step.submit.label}
          </Button>
          <ConsentLine text={step.consent} />
        </form>
      )}
    </div>
  );
}

function ConsentLine({ text }: { text: RichText }) {
  return (
    <p className="font-body text-body-b3 text-text-tertiary">
      {text.map((part, i) =>
        typeof part === "string" ? (
          part
        ) : (
          <AppLink
            // biome-ignore lint/suspicious/noArrayIndexKey: parts are fixed prose, never reordered.
            key={i}
            href={part.link.href}
            className={cn(
              "rounded-xs text-text-secondary underline underline-offset-4 hover:text-text-primary",
              FOCUS_RING,
            )}
          >
            {part.text}
          </AppLink>
        ),
      )}
    </p>
  );
}
